import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';

import {
  ScaleButton,
  ScaleLoadingSpinner,
  ScaleLogo,
  ScaleNotificationBanner,
  ScaleNotificationMessage,
  ScaleTextField,
} from '@cloudorder/ui';

import {
  useCreateTenantCustomer,
  useTenantChoices,
  useTenantCustomerMasterData,
} from '@/shared/api/queries';
import { resetTransportState } from '@/shared/api/http/httpTransport';
import { useAuth } from '@/shared/auth/AuthContext';
import { useVocabulary } from '@/shared/domain/useVocabulary';
import { LocaleSwitcher } from '@/shared/i18n';
import { searchFieldStyles } from '@/shared/ui/DataGrid';
import { RouteLoading } from '@/shared/ui/RouteStatus';
import { useListPaging } from '@/shared/ui/useListPaging';
import { TenantDetailsFields } from '@/pages/admin/TenantDetailsFields';

import styles from './SelectTenantPage.module.css';

/* ── First-tenant inline create form ────────────────────────────────────── *
 *
 * Shown only when an administrator lands on the picker and the deployment has
 * zero tenants. Navigating to /admin/tenants was the old answer, but that
 * route sits behind TenantGuard — which can never be satisfied with zero
 * tenants — so the admin would end up redirected back here, stuck in a loop.
 *
 * Keeping the form inline means the guard is never asked to let an admin
 * through to a tenant-scoped screen when there is no tenant yet. After the
 * first tenant is saved the choices query is refetched, the list appears, and
 * the admin chooses it exactly as any other tenant.
 */
function FirstTenantInlineForm({ onCreated }: { onCreated: () => void }): ReactElement {
  const { t } = useTranslation(['tenancy', 'common']);
  const masterData = useTenantCustomerMasterData();
  const create = useCreateTenantCustomer();

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');

  const trimmedName = name.trim();
  const trimmedCountry = country.trim();
  const trimmedLanguage = language.trim();

  function submit(): void {
    if (trimmedName === '') return;
    create.mutate(
      {
        name: trimmedName,
        ...(trimmedCountry === '' ? {} : { countryCode: trimmedCountry }),
        ...(trimmedLanguage === '' ? {} : { defaultLanguage: trimmedLanguage }),
      },
      { onSuccess: onCreated },
    );
  }

  return (
    <div className={styles.inlineForm}>
      <p className={styles.emptyTitle}>{t('tenancy:select.noneAdminTitle')}</p>
      <p className={styles.emptyBody}>{t('tenancy:select.noneAdminBody')}</p>

      {masterData.data ? (
        <>
          <div className={styles.inlineFormFields}>
            <TenantDetailsFields
              name={name}
              country={country}
              language={language}
              countries={masterData.data.countries}
              languages={masterData.data.languages}
              nameLabel={t('tenancy:tenants.form.name')}
              nameHelp={t('tenancy:tenants.form.nameHelp')}
              countryLabel={t('tenancy:tenants.form.country')}
              countryHelp={t('tenancy:tenants.form.countryHelp')}
              languageLabel={t('tenancy:tenants.form.language')}
              languageHelp={t('tenancy:tenants.form.languageHelp')}
              onNameChange={setName}
              onCountryChange={setCountry}
              onLanguageChange={setLanguage}
            />
          </div>

          {create.error ? (
            <ScaleNotificationMessage variant="error" opened>
              {t('tenancy:tenants.form.failed')}
              <div slot="text">{create.error.message}</div>
            </ScaleNotificationMessage>
          ) : null}

          <ScaleButton
            size="small"
            variant="primary"
            disabled={trimmedName === '' || create.isPending}
            onClick={submit}
          >
            {create.isPending
              ? t('tenancy:tenants.form.submitting')
              : t('tenancy:select.noneAdminAction')}
          </ScaleButton>
        </>
      ) : masterData.error ? (
        <ScaleNotificationMessage variant="error" opened>
          {t('common:state.error')}
          <div slot="text">{(masterData.error as Error).message}</div>
        </ScaleNotificationMessage>
      ) : (
        <RouteLoading label={t('common:state.loadingNamed', { what: t('tenancy:tenants.loading') })} />
      )}
    </div>
  );
}

/* Which tenant am I working in?
 *
 * The one question the cockpit cannot answer for the operator, and the reason it
 * is asked here rather than inferred: an operator may hold access to several
 * tenants, an administrator holds access to all of them, and every screen behind
 * this one — every queue, every case, every count — is an answer *about one
 * tenant*. Guessing would produce a cockpit that is quietly about the wrong
 * customer, which is worse than a cockpit that asks.
 *
 * Choosing re-seals the session cookie against the chosen tenant; it is a write,
 * not a piece of client state, which is what makes it survive a reload and what
 * makes it impossible for the browser to widen its own scope.
 *
 * The screen doubles as the switcher — the top bar links straight here — so it
 * must not bounce an operator who already holds a tenant. That is also why the
 * caches are emptied explicitly below rather than by reloading the page: every
 * row React Query is holding was fetched under the previous tenant, and showing
 * it for even one frame after the switch would be showing another customer's
 * work.
 *
 * The list is bounded, which matters most for the administrator this screen is
 * hardest on: they reach every tenant in the deployment, so their picker is as
 * long as the customer table. Past `FILTERABLE` names a filter box appears and
 * the service does the matching; at most `PICKS` are drawn, with a line saying
 * how many are not. The list scrolls within the card rather than growing it,
 * because a card taller than the window puts the question at the top out of
 * sight of the answers at the bottom.
 */

/** How many choices are drawn at once, and the count past which the filter box
    appears. The first is generous because scrolling a short list is easier than
    typing; the second is low because a filter that shows up only when the list
    is already unmanageable has arrived too late to help. */
const PICKS = 20;
const FILTERABLE = 6;

/** The status a tenant can actually be chosen in. The service refuses the rest;
    this disables them, so a refusal never arrives as a surprise. */
const ACTIVE = 'active';

/**
 * Where to land once a tenant is chosen.
 *
 * Same rule and same reasoning as `LoginPage.safeNext` — the value arrives in a
 * query string, and a leading `//` or `/\` is a protocol-relative URL wearing a
 * local path's clothes.
 *
 * The top bar's switcher deliberately links here *without* a `next`, so a switch
 * lands on the dashboard: the page the operator was reading belonged to the
 * tenant they just left, and returning them to it would be returning them to a
 * deep link that now resolves to somebody else's case or to nothing at all.
 */
function safeNext(raw: string | null): string {
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return '/';
  }
  return raw;
}

export function SelectTenantPage(): ReactElement {
  const { selectTenant, logout } = useAuth();
  const listing = useListPaging();
  const choices = useTenantChoices(listing.query);
  const vocabulary = useVocabulary();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['tenancy', 'common', 'app']);

  /* The id being written, not a boolean: it disables the whole list while a
     choice is in flight *and* says which row the spinner belongs to. */
  const [choosing, setChoosing] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /* One-way, for the reason the same latch has on the admin chooser: filtering
     down to two names must not take the box away that got you there. */
  const filterable = useRef(false);
  if ((choices.data?.total ?? 0) > FILTERABLE) filterable.current = true;

  const tenants = choices.data?.tenants ?? [];
  const selectedId = choices.data?.selectedId ?? null;

  /* The tenant the session is already sealed against goes first when it is in
     the answer at all. It is the one row on this screen whose meaning is "you
     are here", and an operator who opened the switcher by accident leaves by
     clicking it — which they can only do if they can find it. Alphabetical
     order puts it wherever its name falls, which on a long list is off the
     bottom of what is drawn. */
  const ordered = useMemo(() => {
    const current = tenants.find((tenant) => tenant.id === selectedId);
    if (current === undefined) return tenants;
    return [current, ...tenants.filter((tenant) => tenant !== current)];
  }, [tenants, selectedId]);

  const shown = ordered.slice(0, PICKS);
  const hidden = (choices.data?.total ?? 0) - shown.length;

  const next = safeNext(searchParams.get('next'));

  async function choose(id: string): Promise<void> {
    if (choosing !== null) return;
    setChoosing(id);
    setFailed(null);
    try {
      await selectTenant(id);
      /* Both, and in this order. `client.clear()` drops every cached query;
         `resetTransportState()` drops the transport's own in-flight and
         gathered state, which React Query knows nothing about and which would
         otherwise resolve a request started under the old scope into the new
         one. Only then is it safe to render a screen. */
      client.clear();
      resetTransportState();
      await navigate(next, { replace: true });
    } catch (error) {
      setFailed(error instanceof Error ? error.message : t('tenancy:select.failed'));
      setChoosing(null);
    }
  }

  if (choices.error) throw choices.error;

  return (
    <div className={styles.page}>
      <span className={styles.localeToggle}>
        <LocaleSwitcher />
      </span>

      <main className={styles.card}>
        <div className={styles.badge}>
          <ScaleLogo className={styles.badgeLogo} size={28} />
          <div className={styles.badgeText}>
            <span className={styles.badgeName}>PIXIE</span>
            <span className={styles.badgeSub}>CLOUD ORDER MANAGEMENT</span>
          </div>
        </div>

        <h1 className={styles.heading}>{t('tenancy:select.heading')}</h1>
        <p className={styles.sub}>{t('tenancy:select.sub')}</p>

        {failed !== null ? (
          <div className={styles.banner}>
            <ScaleNotificationBanner variant="error" dismissible onScaleClose={() => setFailed(null)}>
              {failed}
            </ScaleNotificationBanner>
          </div>
        ) : null}

        {!choices.data ? (
          <RouteLoading
            label={t('common:state.loadingNamed', { what: t('tenancy:select.loading') })}
          />
        ) : choices.data.total === 0 && listing.term === '' ? (
          /* Not an error, and not a screen to retry from. Somebody signed in
             successfully and holds no grant — the fix is an administrator, not
             another click — so the page says so and offers the only action that
             changes anything from here.

             Gated on an empty filter, because a filter that matches nothing is
             a different fact with a different answer: type something else.

             Two shapes share this empty state:
             - An operator nobody has let in yet (allTenants === false) — only
               an administrator can move them forward, so the page offers sign-out.
             - An administrator who can see every tenant (allTenants === true)
               but the deployment still has none — the only forward path is to
               create the first one. The route lives outside TenantGuard on
               purpose: with zero tenants the guard can never be satisfied, so
               the picker must hand the admin a way through itself. */
          <div className={styles.empty}>
            {choices.data.allTenants ? (
              <FirstTenantInlineForm onCreated={() => { choices.refetch().catch(() => undefined); }} />
            ) : (
              <>
                <p className={styles.emptyTitle}>{t('tenancy:select.noneTitle')}</p>
                <p className={styles.emptyBody}>{t('tenancy:select.noneBody')}</p>
                <ScaleButton size="small" variant="secondary" onClick={logout}>
                  {t('app:profile.signOut')}
                </ScaleButton>
              </>
            )}
          </div>
        ) : (
          <>
            {filterable.current ? (
              <div className={styles.filter}>
                <ScaleTextField
                  label={t('tenancy:select.filter')}
                  hideLabelVisually
                  placeholder={t('tenancy:select.filter')}
                  inputModeType="search"
                  styles={searchFieldStyles}
                  value={listing.term}
                  onScaleChange={(event) => listing.onTerm(String(event.detail.value ?? ''))}
                />
              </div>
            ) : null}

            <ul className={styles.list} aria-label={t('tenancy:select.listLabel')}>
              {shown.map((tenant) => {
                const selectable = tenant.status.toLowerCase() === ACTIVE;
                const current = tenant.id === selectedId;
                return (
                  <li key={tenant.id}>
                    <button
                      type="button"
                      className={styles.choice}
                      /* Named by the customer, never by the id. The id is the
                         value this click sends and is not a thing an operator
                         could recognise, so it stays in the handler. */
                      disabled={!selectable || choosing !== null}
                      aria-current={current ? 'true' : undefined}
                      onClick={() => void choose(tenant.id)}
                    >
                      <span className={styles.choiceName}>{tenant.name}</span>
                      <span className={styles.choiceMeta}>
                        {current ? t('tenancy:select.current') : null}
                        {!selectable ? vocabulary.customerStatus(tenant.status) : null}
                        {choosing === tenant.id ? (
                          <ScaleLoadingSpinner size="small" />
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {shown.length === 0 ? (
              <p className={styles.note}>{t('tenancy:select.noneMatch')}</p>
            ) : hidden > 0 ? (
              <p className={styles.note}>{t('tenancy:select.more', { count: hidden })}</p>
            ) : null}

            {/* An administrator reaches every tenant without anybody granting
                them one, and the list would otherwise look like a very generous
                set of grants. Saying which of the two it is costs one line. */}
            {choices.data.allTenants ? (
              <p className={styles.note}>{t('tenancy:select.allTenants')}</p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
